"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createLayerLink = exports.setOperationalLayers = void 0;
var tslib_1 = require("tslib");
var webMercatorUtils_1 = tslib_1.__importDefault(require("esri/geometry/webMercatorUtils"));
var LayerSettings_1 = require("./LayerSettings");
/**
 * Updates the layers' settings to match those from URL search parameters.
 * This function should be run on the operational layers **before** they are added
 * to a LayerList.
 * @param search URL search string
 * @param opLayers Operational layers
 */
function setOperationalLayers(search, opLayers) {
    var e_1, _a;
    if (!search || !window.URLSearchParams) {
        return;
    }
    try {
        for (var opLayers_1 = tslib_1.__values(opLayers), opLayers_1_1 = opLayers_1.next(); !opLayers_1_1.done; opLayers_1_1 = opLayers_1.next()) {
            var opLayer = opLayers_1_1.value;
            // Skip if layer has no corresponding entry in URLSearchParams.
            if (!opLayer.id || !search.has(opLayer.id)) {
                continue;
            }
            var val = search.get(opLayer.id);
            if (!val) {
                // Skip if there is no value. (E.g., value is empty string.)
                continue;
            }
            // parse string value into layer settings object.
            var layerSettings = LayerSettings_1.LayerSettings.parse(val);
            if (!layerSettings) {
                // tslint:disable-next-line:no-console
                console.warn("could not parse \"".concat(val, "\" into layer settings for layer ").concat(opLayer.id, "."));
                continue;
            }
            layerSettings.apply(opLayer);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (opLayers_1_1 && !opLayers_1_1.done && (_a = opLayers_1.return)) _a.call(opLayers_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
}
exports.setOperationalLayers = setOperationalLayers;
/**
 * Creates a link on the map that will, when clicked, copy the URL of the page with
 * URL search parameters.
 *
 * CSS classes of output elements:
 *
 *  class name           | description
 *  ------------------   | -----------
 * .layer-link           | this class will be applied to the div element that contains the link.
 * .layer-link--copied   | This class will be added to the div after the user has clicked the link and successfully updated the browsers URL via History API. Removed after user changes the state of the map and the URL is updated.
 * .layer-link--disabled | This class is applied initially to the div and is removed once the user has changed the map in a way that updates the link's URL.
 * .layer-link__anchor   | this class is applied to the anchor element.
 *
 * @param layerList A layer list control
 * @returns An object that contains the "root" HTML element control and the event handlers used to update the URL to match the map's state.
 */
function createLayerLink(layerList) {
    var urlSupported = window.URL && window.URLSearchParams && window.history;
    if (!urlSupported) {
        throw new Error("Browser must support all of the following features: URL, URLSearchParams, history.");
    }
    var div = document.createElement("div");
    div.classList.add("layer-link");
    var a = document.createElement("a");
    a.href = new URL(location.href, location.href.replace(/\?.+$/, "")).toString();
    a.textContent = "🔗";
    a.title = "Click to update URL with layer and extent paramters.";
    a.target = "_blank";
    a.classList.add("layer-link__anchor");
    var copiedClass = "layer-link--copied";
    var disabledClass = "layer-link--disabled";
    div.classList.add(disabledClass);
    div.appendChild(a);
    // Setup event to update the "map-extent" URL search parameter when the map's extent is changed.
    var extentChangeHandle = layerList.map.on("extent-change", function (_a) {
        var extent = _a.extent;
        extent = webMercatorUtils_1.default.webMercatorToGeographic(extent);
        // Get the coordinates of the new extent.
        var xmin = extent.xmin, ymin = extent.ymin, xmax = extent.xmax, ymax = extent.ymax;
        // round to 4 dec. places and concatenate into space-separated string.
        var searchValue = [xmin, ymin, xmax, ymax]
            .map(function (n) {
            return Math.round(n * 10000) / 10000;
        })
            .join(" ");
        // Add or update the "map-extent" property with the coordinates string,
        // then set the anchor's href to this new URL.
        var url = new URL(a.href);
        url.searchParams.set("map-extent", searchValue);
        a.href = url.toString();
        // remove the disabled and copied classes to indicate to the user that the link href has been updated
        // since they last copied.
        div.classList.remove(disabledClass);
        div.classList.remove(copiedClass);
    });
    // Setup event handler to update URL when map layers are toggled on or off.
    var layerToggleHandle = layerList.on("toggle", function (toggleEvent) {
        console.group("LayerList toggle event");
        // Enable and turn off copied status.
        div.classList.remove(copiedClass);
        div.classList.remove(disabledClass);
        var layerIndex = toggleEvent.layerIndex, visible = toggleEvent.visible, subLayerIndex = toggleEvent.subLayerIndex;
        console.debug("layer: ".concat(layerIndex, ", sublayer ").concat(subLayerIndex, ", visible: ").concat(visible));
        // Get the operational layer from the layer list based on the event's layerIndex.
        var operationalLayer = layerList.layers[layerIndex];
        // Get the URL currently in the link's href attribute.
        // Store in URL object so that search params can be
        // easily searched and manipulated.
        var url = new URL(a.href);
        // Retrieve the current settings store in the URL for this layer.
        // If there are no settings, the old settings variable will be null.
        var oldSettingStr = url.searchParams.get(operationalLayer.id);
        var oldSettings = oldSettingStr
            ? LayerSettings_1.LayerSettings.parse(oldSettingStr)
            : null;
        // Detect if the settings for this layer had sublayers specified.
        var hadSublayersDefined = oldSettings
            ? !!oldSettings.visibleLayers
            : false;
        var layerSettings = new LayerSettings_1.LayerSettings({
            visible: operationalLayer.visibility,
            visibleLayers: operationalLayer.layer
                .visibleLayers
        });
        // Initialize layer settings object.
        // let layerSettings: LayerSettings;
        // Layer settings string for the URL will simply indicate if
        // the parent layer is on or off except under these conditions:
        // * Ability to change sublayer visibility is enabled for current layer.
        // * User changed visibility of sublayer via UI or sublayer visibility
        //   had been previously set in the URL.
        // if (
        //   operationalLayer.showSubLayers &&
        //   (hadSublayersDefined || subLayerIndex != null)
        // ) {
        //   layerSettings = new LayerSettings(operationalLayer.layer);
        // } else {
        //   layerSettings = new LayerSettings({ visible });
        // }
        // Update the URL search parameter for this layer and assign updated URL to link's href.
        url.searchParams.set(operationalLayer.id, layerSettings.toString());
        a.href = url.toString();
    });
    // Setup link click event. When link is clicked, URL will be updated.
    a.onclick = function (e) {
        if (window.history) {
            history.replaceState(undefined, document.title, a.href);
            div.classList.add(copiedClass);
            return false;
        }
    };
    var mapRoot = layerList.map.root;
    mapRoot.appendChild(div);
    return { layerToggleHandle: layerToggleHandle, extentChangeHandle: extentChangeHandle, root: div };
}
exports.createLayerLink = createLayerLink;
